/**
 * Gate de módulo: "esse papel alcança esse recurso?" (T57, CAT-07).
 *
 * É a pergunta que decide se um menu ou um botão aparece, e ela é diferente de
 * "essa pessoa pode nesse registro". Confundir as duas é o defeito **P1-5** do
 * spec, e ele tem duas metades:
 *
 * 1. **O falso positivo.** `ability.can(acao, "RDO")` com sujeito string faz o
 *    CASL responder pelo TIPO: `matchesConditions` devolve `true` sem olhar a
 *    condição. Uma regra que só concede ao site manager do diário libera o botão
 *    para todo mundo — e o clique depois falha, ou pior, não falha.
 * 2. **O custo.** A alternativa óbvia, `possibleRulesFor(...).some(r => !r.conditions)`,
 *    varre a lista de regras a cada chamada. Com o acúmulo medido no spec (12.512
 *    regras) isso é varredura em laço de render.
 *
 * A resposta das duas é a mesma: **pré-computar o alcance como um `Set`**, uma vez,
 * na compilação do catálogo. Concessão condicional não entra no conjunto, então o
 * falso positivo deixa de ser possível por construção; e a consulta vira lookup.
 *
 * **Onde isto é ligado.** Ainda em lugar nenhum: `index.ts` monta a Ability a
 * partir dos dezoito arquivos imperativos, e trocar a fonte é a **T56**. Este
 * módulo é a peça, testada contra o catálogo real; a troca do fio é lá.
 */
import { ACOES, RECURSOS, type Acao, type Recurso } from "./catalogo/vocabulario";
import type { ConcessaoLegivel } from "./compilador";

/**
 * ` ` como separador, e não `.` ou `:`: nomes de ação e de recurso do
 * vocabulário contêm `_` e `-` (`aviso-atraso`, `upload_dp`, `OS_FIELD`), e um
 * separador que possa aparecer nos nomes deixa `("a-b", "c")` colidir com
 * `("a", "b-c")`.
 *
 * **Exportada na T129, e não é só conveniência.** Até então era privada deste
 * módulo, e `index.ts` precisou juntar uma chave equivalente na mão para
 * mesclar `CONCESSOES_CUSTOMIZADAS` incondicionais no alcance de
 * `podeNoModulo` — duas implementações do mesmo formato, em dois arquivos.
 * Medido nesta tarefa: o byte entre os dois interpolados aqui era um NUL
 * (`\0`), não um espaço — corrupção antiga, congelada desde a T57 e nunca
 * pega porque `montarAlcanceDeModulo` e `alcancaModulo` sempre usaram a MESMA
 * função corrompida dos dois lados (grava e lê com o mesmo separador errado, e
 * por isso concordam entre si). Só ficou visível quando `index.ts` — com um
 * separador reescrito à mão, um espaço de verdade — deixou de concordar com
 * ela. Corrigido para espaço; exportada para que só exista UMA definição de
 * "chave", e a classe inteira de bug (duas cópias divergindo) não volte.
 */
export function chave(acao: string, recurso: string) {
  return `${acao} ${recurso}`;
}

/**
 * Uma concessão é condicional quando o que ela concede depende do REGISTRO —
 * relação, estado ou condição livre. Nenhuma delas responde "esse papel alcança
 * esse recurso", então nenhuma entra no gate.
 *
 * `condicao` está aqui junto de `exigeRelacao` e `estados`, e o texto da tarefa
 * só cita as duas primeiras. É a mesma classe: `{ area: "FIELD" }` é condição
 * sobre o registro tanto quanto `estados`. Deixá-la de fora reabriria o falso
 * positivo pela terceira porta.
 */
function ehCondicional(concessao: ConcessaoLegivel) {
  return Boolean(
    concessao.exigeRelacao?.length ||
    concessao.estados?.length ||
    (concessao.condicao && Object.keys(concessao.condicao).length),
  );
}

/**
 * O alcance de módulo de um conjunto de concessões, pré-computado.
 *
 * Duas expansões, e as duas existem porque o CASL as faz e o gate tem de decidir
 * igual para o caso incondicional:
 *
 * - **`manage` alcança toda ação** naquele recurso. É o que faz o botão de criar
 *   RDO continuar visível para quem tem `rdo.admin`, cujo papel concede `manage`
 *   e não `create`.
 * - **o recurso `all` alcança todo recurso.** É o `can("manage", "all")` de
 *   `acesso.total` e o `can("read", "all")` do grupo MKT.
 */
export function montarAlcanceDeModulo(
  concessoes: readonly ConcessaoLegivel[],
): ReadonlySet<string> {
  const alcance = new Set<string>();

  for (const concessao of concessoes) {
    if (ehCondicional(concessao)) continue;

    const acoes: readonly string[] = concessao.acoes.includes("manage") ? ACOES : concessao.acoes;
    const recursos: readonly string[] =
      concessao.recurso === "all" ? RECURSOS : [concessao.recurso];

    for (const recurso of recursos) {
      for (const acao of acoes) alcance.add(chave(acao, recurso));
    }
  }

  return alcance;
}

/**
 * Consulta o alcance. Lookup em `Set`, sem varredura de regra.
 *
 * Aqui o recurso como string é legítimo — é a pergunta certa. Para "essa pessoa
 * pode nesse registro", use `pode(acao, registro)`, que exige o registro.
 */
export function alcancaModulo(alcance: ReadonlySet<string>, acao: Acao, recurso: Recurso): boolean {
  return alcance.has(chave(acao, recurso));
}
